import React from 'react'
import { Check, Loader2 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { ThinkingStep } from '../types'

interface TypingIndicatorProps {
  status?: string | null
  steps?: ThinkingStep[]
}

export const TypingIndicator: React.FC<TypingIndicatorProps> = ({ status, steps = [] }) => {
  const { currentTheme } = useTheme()
  const label = status ?? 'Thinking'
  const hasSteps = steps.length > 0

  return (
    <div className="px-4 py-2 max-w-3xl mx-auto w-full">
      {/* Header — matches ChatMessage's AI avatar treatment, no card */}
      <div className="flex items-center gap-2.5 mb-3">
        <span
          className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: currentTheme.userMessageBackground }}
        >
          <img src="/Zeph-White-Logo.png" alt="Zeph" className="w-[70%] h-[70%] object-contain" />
        </span>
        <span
          className="text-sm font-semibold"
          style={{
            background:
              'linear-gradient(90deg, #6366f1 0%, #a78bfa 40%, #6366f1 80%, #a78bfa 100%)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            animation: 'zephThinkShimmer 2.4s linear infinite',
          }}
        >
          {label}
        </span>
        <div className="flex items-center gap-0.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="w-1 h-1 rounded-full"
              style={{
                backgroundColor: '#818cf8',
                animation: `zephThinkDot 1.4s ease-in-out ${i * 0.2}s infinite`,
              }}
            />
          ))}
        </div>
      </div>

      {hasSteps ? (
        /* Live tool steps replacing shimmer */
        <div className="flex flex-col gap-2 pl-[38px]">
          {steps.map(step => (
            <div key={step.id} className="flex items-center gap-2.5">
              {step.status === 'running' ? (
                <Loader2
                  className="w-3.5 h-3.5 shrink-0 animate-spin"
                  style={{ color: '#818cf8' }}
                />
              ) : (
                <Check
                  className="w-3.5 h-3.5 shrink-0"
                  style={{ color: '#34d399' }}
                />
              )}
              <span
                className="text-xs"
                style={{
                  color: step.status === 'done' ? currentTheme.mutedText : currentTheme.text,
                  transition: 'color 0.2s',
                }}
              >
                {step.label}
              </span>
            </div>
          ))}
        </div>
      ) : (
        /* Default shimmer skeleton when no tool steps yet */
        <div className="flex flex-col gap-2 pl-[38px]">
          {[65, 80, 50].map((w, i) => (
            <div
              key={i}
              className="h-2.5 rounded-full"
              style={{
                width: `${w}%`,
                background: `linear-gradient(90deg, ${currentTheme.border} 25%, ${currentTheme.hoverBackground} 50%, ${currentTheme.border} 75%)`,
                backgroundSize: '200% auto',
                animation: `zephThinkShimmer ${1.8 + i * 0.15}s linear infinite`,
                animationDelay: `${i * 0.1}s`,
                opacity: 0.6,
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}
